/**
 * Centralized logging utility for the application
 * Handles environment-based logging and error tracking
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

class Logger {
  private isDevelopment: boolean;
  private enableDebugLogging: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV || false;
    this.enableDebugLogging = 
      import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true' || 
      this.isDevelopment;
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, context?: LogContext, ...args: unknown[]): void {
    if (this.enableDebugLogging) {
      console.log(`[DEBUG] ${message}`, context || '', ...args);
    }
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: LogContext, ...args: unknown[]): void {
    if (this.enableDebugLogging) {
      console.info(`[INFO] ${message}`, context || '', ...args);
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, context?: LogContext, ...args: unknown[]): void {
    console.warn(`[WARN] ${message}`, context || '', ...args);
    
    // In production, send to error tracking service
    if (!this.isDevelopment) {
      this.trackError('warn', message, context);
    }
  }

  /**
   * Log error messages
   */
  error(error: Error | string, context?: LogContext, ...args: unknown[]): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    console.error(`[ERROR] ${errorMessage}`, context || '', errorStack || '', ...args);
    
    // In production, send to error tracking service
    if (!this.isDevelopment) {
      this.trackError('error', errorMessage, {
        ...context,
        stack: errorStack,
      });
    }
  }

  /**
   * Track errors to external service (Sentry, LogRocket, etc.)
   * This is a placeholder - integrate with your error tracking service
   */
  private trackError(level: LogLevel, message: string, context?: LogContext): void {
    // TODO: Integrate with error tracking service
    // Example: Sentry.captureException(new Error(message), { extra: context });
    
    // For now, we can store in localStorage for debugging (remove in production)
    if (this.isDevelopment) {
      try {
        const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
        errors.push({
          level,
          message,
          context,
          timestamp: new Date().toISOString(),
        });
        // Keep only last 10 errors
        const recentErrors = errors.slice(-10);
        localStorage.setItem('app_errors', JSON.stringify(recentErrors));
      } catch (e) {
        // Silently fail if localStorage is not available
      }
    }
  }

  /**
   * Create a scoped logger for a specific component
   */
  createScope(component: string) {
    return {
      debug: (message: string, context?: Omit<LogContext, 'component'>, ...args: unknown[]) =>
        this.debug(message, { ...context, component }, ...args),
      info: (message: string, context?: Omit<LogContext, 'component'>, ...args: unknown[]) =>
        this.info(message, { ...context, component }, ...args),
      warn: (message: string, context?: Omit<LogContext, 'component'>, ...args: unknown[]) =>
        this.warn(message, { ...context, component }, ...args),
      error: (error: Error | string, context?: Omit<LogContext, 'component'>, ...args: unknown[]) =>
        this.error(error, { ...context, component }, ...args),
    };
  }
}

// Export singleton instance
export const logger = new Logger();

// Export scoped logger factory
export const createLogger = (component: string) => logger.createScope(component);

// Export default logger
export default logger;









